import React, { useState, useCallback, useMemo, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  format,
  parseISO,
  addMonths,
  addDays,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  getDate,
  isBefore,
  isAfter,
  isSameDay,
  isToday,
  startOfDay,
} from 'date-fns';
import IconSystem from 'icon-collection/icon-systems';
import './DateRangePicker.css';

// ============================================================================
// TYPES
// ============================================================================

export interface DateRangePickerProps {
  checkInDate: string;
  checkOutDate: string;
  onChange: (checkIn: string, checkOut: string) => void;
  minDate?: string;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
  label?: string | React.ReactNode;
}

// ============================================================================
// MONTH GRID SUB-COMPONENT
// ============================================================================

interface MonthGridProps {
  month: Date;
  minDate: Date;
  rangeStart: Date | null;
  rangeEnd: Date | null;
  selectionPhase: 'idle' | 'selecting';
  onDayClick: (day: Date) => void;
  onDayHover: (day: Date) => void;
}

const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const MonthGrid: React.FC<MonthGridProps> = ({
  month, minDate, rangeStart, rangeEnd, selectionPhase, onDayClick, onDayHover,
}) => {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDayOfWeek = getDay(monthStart);

  // Sort range for display
  let displayStart = rangeStart;
  let displayEnd = rangeEnd;
  if (displayStart && displayEnd && isAfter(displayStart, displayEnd)) {
    const tmp = displayStart;
    displayStart = displayEnd;
    displayEnd = tmp;
  }

  return (
    <div className="date-range-month">
      <div className="date-range-month-title">{format(month, 'MMMM yyyy')}</div>
      <div className="date-range-weekdays">
        {DAY_LABELS.map(d => <div key={d} className="date-range-weekday">{d}</div>)}
      </div>
      <div className="date-range-days">
        {/* Empty padding cells for first week */}
        {Array.from({ length: startDayOfWeek }).map((_, i) => (
          <div key={`e-${i}`} className="date-range-day date-range-day--empty" />
        ))}
        {/* Day cells */}
        {days.map(day => {
          const dayStart = startOfDay(day);
          const isDisabled = isBefore(dayStart, minDate);
          const isStart = displayStart ? isSameDay(day, displayStart) : false;
          const isEnd = displayEnd ? isSameDay(day, displayEnd) : false;
          const isInRange = displayStart && displayEnd
            ? isAfter(day, displayStart) && isBefore(day, displayEnd) : false;
          const isTodayDate = isToday(day);
          const isPreview = selectionPhase === 'selecting' && !isStart && !isEnd && isInRange;
          const isConfirmedRange = selectionPhase === 'idle' && !isStart && !isEnd && isInRange;

          const classes = [
            'date-range-day',
            isDisabled && 'date-range-day--disabled',
            isStart && 'date-range-day--range-start',
            isEnd && 'date-range-day--range-end',
            isConfirmedRange && 'date-range-day--in-range',
            isPreview && 'date-range-day--preview',
            isTodayDate && !isStart && !isEnd && 'date-range-day--today',
          ].filter(Boolean).join(' ');

          return (
            <div
              key={day.toISOString()}
              className={classes}
              onClick={() => !isDisabled && onDayClick(day)}
              onMouseEnter={() => !isDisabled && selectionPhase === 'selecting' && onDayHover(day)}
            >
              {getDate(day)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  checkInDate,
  checkOutDate,
  onChange,
  minDate,
  disabled = false,
  error,
  placeholder = 'Select dates...',
  label,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [baseMonth, setBaseMonth] = useState<Date>(() => {
    if (checkInDate) return startOfMonth(parseISO(checkInDate));
    return startOfMonth(new Date());
  });
  const [selectionPhase, setSelectionPhase] = useState<'idle' | 'selecting'>('idle');
  const [tempStart, setTempStart] = useState<Date | null>(null);
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // --- Min date ---
  const minDateObj = useMemo(() => {
    if (minDate) return startOfDay(parseISO(minDate));
    return startOfDay(new Date());
  }, [minDate]);

  // --- Display text ---
  const displayText = useMemo(() => {
    if (!checkInDate && !checkOutDate) return '';
    const parts: string[] = [];
    parts.push(checkInDate ? format(parseISO(checkInDate), 'dd MMM yyyy') : '--');
    parts.push('\u2192');
    parts.push(checkOutDate ? format(parseISO(checkOutDate), 'dd MMM yyyy') : '--');
    return parts.join(' ');
  }, [checkInDate, checkOutDate]);

  // --- Effective range for display ---
  const effectiveRangeStart = useMemo(() => {
    if (selectionPhase === 'selecting') return tempStart;
    if (checkInDate) return parseISO(checkInDate);
    return null;
  }, [selectionPhase, tempStart, checkInDate]);

  const effectiveRangeEnd = useMemo(() => {
    if (selectionPhase === 'selecting') return hoveredDate;
    if (checkOutDate) return parseISO(checkOutDate);
    return null;
  }, [selectionPhase, hoveredDate, checkOutDate]);

  // --- Position calculation (Phase 1: below trigger) ---
  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownWidth = 600;
    const vw = window.innerWidth;

    let top = rect.bottom + 4;
    let left = rect.left;

    if (left + dropdownWidth > vw - 8) {
      left = Math.max(8, vw - dropdownWidth - 8);
    }

    setDropdownPos({ top, left });
  }, []);

  // --- Open ---
  const handleOpen = useCallback(() => {
    if (disabled) return;
    // Reset baseMonth to checkInDate's month if available
    if (checkInDate) {
      setBaseMonth(startOfMonth(parseISO(checkInDate)));
    } else {
      setBaseMonth(startOfMonth(new Date()));
    }
    calculatePosition();
    setIsOpen(true);
  }, [disabled, checkInDate, calculatePosition]);

  // --- Close ---
  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSelectionPhase('idle');
    setTempStart(null);
    setHoveredDate(null);
  }, []);

  // --- Phase 2: reposition via useLayoutEffect ---
  useLayoutEffect(() => {
    if (!isOpen || !dropdownRef.current || !triggerRef.current) return;

    const menuRect = dropdownRef.current.getBoundingClientRect();
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    let newTop = dropdownPos.top;
    let newLeft = dropdownPos.left;
    let adjusted = false;

    if (menuRect.right > vw - 8) {
      newLeft = Math.max(8, vw - menuRect.width - 8);
      adjusted = true;
    }

    if (menuRect.bottom > vh - 8) {
      newTop = triggerRect.top - menuRect.height - 4;
      if (newTop < 8) newTop = 8;
      adjusted = true;
    }

    if (adjusted) setDropdownPos({ top: newTop, left: newLeft });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // --- Close on Escape, scroll, resize ---
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDropdown();
    };
    const handleScrollResize = () => closeDropdown();

    window.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleScrollResize);
    window.addEventListener('scroll', handleScrollResize, true);

    return () => {
      window.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleScrollResize);
      window.removeEventListener('scroll', handleScrollResize, true);
    };
  }, [isOpen, closeDropdown]);

  // --- Day click ---
  const handleDayClick = useCallback((day: Date) => {
    if (selectionPhase === 'idle') {
      setTempStart(day);
      setHoveredDate(day);
      setSelectionPhase('selecting');
    } else {
      let start = tempStart!;
      let end = day;

      // Auto-sort
      if (isAfter(start, end)) {
        const tmp = start;
        start = end;
        end = tmp;
      }

      // Same day: set check-out to next day
      if (isSameDay(start, end)) {
        end = addDays(end, 1);
      }

      onChange(format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd'));
      setSelectionPhase('idle');
      setTempStart(null);
      setHoveredDate(null);
      setIsOpen(false);
    }
  }, [selectionPhase, tempStart, onChange]);

  // --- Month navigation ---
  const handlePrevMonth = useCallback(() => {
    setBaseMonth(prev => addMonths(prev, -1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setBaseMonth(prev => addMonths(prev, 1));
  }, []);

  const isPrevDisabled = useMemo(() => {
    const prevMonth = addMonths(baseMonth, -1);
    return isBefore(endOfMonth(prevMonth), minDateObj);
  }, [baseMonth, minDateObj]);

  // --- Clear ---
  const handleClear = useCallback(() => {
    onChange('', '');
    closeDropdown();
  }, [onChange, closeDropdown]);

  // --- Trigger classes ---
  const triggerClasses = [
    'date-range-trigger',
    disabled && 'date-range-trigger--disabled',
    error && 'date-range-trigger--error',
  ].filter(Boolean).join(' ');

  return (
    <div>
      {label && <label className="form-label">{label}</label>}

      <div
        ref={triggerRef}
        className={triggerClasses}
        onClick={handleOpen}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpen(); }
        }}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <span className={displayText ? 'date-range-trigger-text' : 'date-range-trigger-text date-range-trigger-placeholder'}>
          {displayText || placeholder}
        </span>
        <span className="date-range-trigger-icon">
          <IconSystem.calendar size={16} />
        </span>
      </div>

      {error && <div className="form-error-message">{error}</div>}

      {isOpen && createPortal(
        <>
          {/* Backdrop */}
          <div
            onClick={closeDropdown}
            style={{ position: 'fixed', inset: 0, zIndex: 10199, background: 'transparent' }}
            aria-hidden="true"
          />
          {/* Calendar dropdown */}
          <div
            ref={dropdownRef}
            className="date-range-dropdown"
            style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 10200 }}
          >
            {/* Navigation */}
            <div className="date-range-nav">
              <button
                className="date-range-nav-btn"
                onClick={handlePrevMonth}
                disabled={isPrevDisabled}
                type="button"
                title="Previous month"
              >
                <IconSystem.chevronLeft size={16} />
              </button>
              <span className="date-range-nav-title">
                {format(baseMonth, 'MMMM yyyy')} &mdash; {format(addMonths(baseMonth, 1), 'MMMM yyyy')}
              </span>
              <button
                className="date-range-nav-btn"
                onClick={handleNextMonth}
                type="button"
                title="Next month"
              >
                <IconSystem.chevronRight size={16} />
              </button>
            </div>

            {/* Two month calendars */}
            <div className="date-range-calendars">
              <MonthGrid
                month={baseMonth}
                minDate={minDateObj}
                rangeStart={effectiveRangeStart}
                rangeEnd={effectiveRangeEnd}
                selectionPhase={selectionPhase}
                onDayClick={handleDayClick}
                onDayHover={setHoveredDate}
              />
              <MonthGrid
                month={addMonths(baseMonth, 1)}
                minDate={minDateObj}
                rangeStart={effectiveRangeStart}
                rangeEnd={effectiveRangeEnd}
                selectionPhase={selectionPhase}
                onDayClick={handleDayClick}
                onDayHover={setHoveredDate}
              />
            </div>

            {/* Footer: Clear button */}
            {(checkInDate || checkOutDate) && selectionPhase === 'idle' && (
              <div className="date-range-footer">
                <button className="date-range-footer-btn" onClick={handleClear} type="button">
                  Clear dates
                </button>
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export { DateRangePicker };
